import React, {useContext, useEffect, useMemo, useState} from 'react';
import {AppContext} from '../context/AppContext';
import IssueAutocompleteInput from './IssueAutocompleteInput';
import {Favorite} from '../types/storage';
import {useIssueValidation} from '../hooks/useIssueValidation';
import ConfirmDialog from './ConfirmDialog';

interface FavoritesCardProps {
  onUseFavorite: (fav: Favorite) => void;
}

const FavoritesCard: React.FC<FavoritesCardProps> = ({onUseFavorite}) => {
  const context = useContext(AppContext);
  if (!context) throw new Error('FavoritesCard must be used within AppContextProvider');

  const {i18n, favorites, addFavorite, updateFavorite, removeFavorite, reorderFavorites, searchIssues} = context;

  // Form state
  const [expanded, setExpanded] = useState(false);
  const [issueKey, setIssueKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>('#3b82f6'); // default blue

  // For drag and drop
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Edit mode & per-pill editing state
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editIssueKey, setEditIssueKey] = useState('');
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editColor, setEditColor] = useState<string>('#3b82f6');

  // Delete confirmation state
  const [deleteFavId, setDeleteFavId] = useState<string | null>(null);

  // Centralized validation
  const { isIssueValid, validatingIssue } = useIssueValidation(issueKey, searchIssues);
  const { isIssueValid: isEditIssueValid, validatingIssue: validatingEditIssue } = useIssueValidation(editIssueKey, searchIssues);

  const canSave = useMemo(() => isIssueValid === true, [isIssueValid]);
  const canSaveEdit = useMemo(() => isEditIssueValid === true, [isEditIssueValid]);

  // Auto-exit edit mode when no favorites remain
  useEffect(() => {
    if (editMode && favorites.length === 0) {
      setEditMode(false);
      setEditingId(null);
    }
  }, [editMode, favorites.length]);

  const clearForm = () => {
    setIssueKey('');
    setName('');
    setDescription('');
    setColor('#3b82f6');
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    const fav: Favorite = {
      id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      issueKey: issueKey.trim(),
      name: name.trim() || undefined,
      description: description.trim() || undefined,
      color: color || undefined
    };
    await addFavorite(fav);
    setExpanded(false);
    clearForm();
  };

  // Edit helpers
  const startEditingFavorite = (fav: Favorite) => {
    setEditingId(fav.id);
    setEditIssueKey(fav.issueKey);
    setEditName(fav.name || '');
    setEditDescription(fav.description || '');
    setEditColor(fav.color || '#3b82f6');
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const handleEditSave = async () => {
    if (!canSaveEdit || !editingId) {
      return;
    }
    const original = favorites.find(f => f.id === editingId);
    if (!original) return;
    const updated: Favorite = {
      ...original,
      issueKey: editIssueKey.trim(),
      name: editName.trim() || undefined,
      description: editDescription.trim() || undefined,
      color: editColor || undefined
    };
    await updateFavorite(updated);
    setEditingId(null);
  };

  const onDragStart = (idx: number, e: React.DragEvent) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = async (toIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    const fromIdxStr = e.dataTransfer.getData('text/plain');
    const fromIdx = fromIdxStr ? parseInt(fromIdxStr, 10) : dragIndex;
    if (typeof fromIdx === 'number' && !Number.isNaN(fromIdx)) {
      await reorderFavorites(fromIdx, toIdx);
    }
    setDragIndex(null);
  };

  return (
    <div className={`card${editMode ? ' edit-mode' : ''} favorites-card`}>
      <div className="favorites-row">
        <div className="pill-list">
          {favorites.length === 0 && (
            <span className="pill-empty">
              {i18n?.t ? i18n.t('favoritesEmpty') : 'What are you working on today?'}
            </span>
          )}
          {favorites.map((f, idx) => (
            <div
              key={f.id}
              className={`pill${editMode && editingId === f.id ? ' editing' : ''}`}
              draggable
              onDragStart={(e) => onDragStart(idx, e)}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => onDrop(idx, e)}
              style={{ background: f.color || '#eef2ff' }}
              title={f.description || ''}
            >
              <button
                type="button"
                className="pill-btn"
                onClick={() => {
                  if (editMode) {
                    startEditingFavorite(f);
                  } else {
                    onUseFavorite(f);
                  }
                }}
              >
                <span className="pill-title">
                  {f.name || f.issueKey}
                </span>
              </button>
              <button
                type="button"
                className="pill-btn pill-delete-btn"
                aria-label={i18n?.t ? i18n.t('delete') : 'Delete'}
                onClick={() => setDeleteFavId(f.id)}
                title={i18n?.t ? i18n.t('delete') : 'Delete'}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label={editMode ? (i18n?.t ? i18n.t('exitEditMode') : 'Exit edit mode') : (i18n?.t ? i18n.t('edit') : 'Edit favorites')}
          title={editMode ? (i18n?.t ? i18n.t('exitEditMode') : 'Exit edit mode') : (i18n?.t ? i18n.t('edit') : 'Edit favorites')}
          disabled={favorites.length === 0}
          onClick={() => {
            setEditMode(m => {
              const next = !m;
              if (!next) {
                setEditingId(null);
              }
              return next;
            });
          }}
          className={`icon-btn${editMode ? ' active' : ''}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor" />
            <path d="M20.71 7.04a1.004 1.004 0 0 0 0-1.42l-2.34-2.34a1.004 1.004 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor" />
          </svg>
        </button>

        <button
          type="button"
          aria-label={expanded ? (i18n?.t ? i18n.t('cancel') : 'Cancel') : (i18n?.t ? i18n.t('add') : 'Add favorite')}
          title={expanded ? (i18n?.t ? i18n.t('cancel') : 'Cancel') : (i18n?.t ? i18n.t('add') : 'Add favorite')}
          onClick={() => setExpanded(e => !e)}
          className="icon-btn"
        >
          {expanded ? (
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {expanded && (
        <div className="favorites-form">
          <div className="row">
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('issueKey') : 'Issue key'}</span>
                <IssueAutocompleteInput
                  inputId="favorite-issue"
                  value={issueKey}
                  onChange={setIssueKey}
                  placeholder="PROJ-123"
                  required
                  pattern="[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*"
                  autoComplete="off"
                />
              </label>
              {!validatingIssue && issueKey.trim() && !isIssueValid && (
                <div className="error">
                  {i18n?.t ? i18n.t('invalidOrUnknownIssue') : 'Invalid or unknown issue'}
                </div>
              )}
            </div>
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('name') : 'Name'}</span>
                <input
                  id="favorite-name"
                  type="text"
                  placeholder={i18n?.t ? i18n.t('nameOptional') : 'Optional name'}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
            </div>
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('description') : 'Description'}</span>
                <input
                    id="favorite-desc"
                    type="text"
                    placeholder={i18n?.t ? i18n.t('descPlaceholder') : 'What are you working on?'}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
              </label>
            </div>
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('color') : 'Color'}</span>
                <input
                    id="favorite-color"
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                />
              </label>
            </div>
          </div>
          <div className="row right">
            <div className="field">
              <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setExpanded(false);
                    clearForm();
                  }}
              >
                {i18n?.t ? i18n.t('cancel') : 'Cancel'}
              </button>
            </div>
            <div className="field">
              <button
                  type="button"
                  className="btn primary"
                  disabled={!canSave}
                  onClick={handleSave}
                  title={!canSave ? (i18n?.t ? i18n.t('invalidOrUnknownIssue') : 'Invalid or unknown issue') : undefined}
              >
                {i18n?.t ? i18n.t('save') : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editMode && editingId && (
        <div className="favorites-form">
          <div className="small">
            {(i18n?.t ? i18n.t('editing') : 'Editing')}: <strong>{favorites.find(f => f.id === editingId)?.name || favorites.find(f => f.id === editingId)?.issueKey || ''}</strong>
          </div>
          <div className="row">
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('issueKey') : 'Issue key'}</span>
                <IssueAutocompleteInput
                  inputId="favorite-issue-edit"
                  value={editIssueKey}
                  onChange={setEditIssueKey}
                  placeholder="PROJ-123"
                  required
                  pattern="[A-Za-z][A-Za-z0-9_]+-[1-9][0-9]*"
                  autoComplete="off"
                />
              </label>
              {!validatingEditIssue && editIssueKey.trim() && !isEditIssueValid && (
                <div className="error">
                  {i18n?.t ? i18n.t('invalidOrUnknownIssue') : 'Invalid or unknown issue'}
                </div>
              )}
            </div>
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('name') : 'Name'}</span>
                <input
                  id="favorite-name-edit"
                  type="text"
                  placeholder={i18n?.t ? i18n.t('nameOptional') : 'Optional name'}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </label>
            </div>
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('description') : 'Description'}</span>
                <input
                    id="favorite-desc-edit"
                    type="text"
                    placeholder={i18n?.t ? i18n.t('descPlaceholder') : 'What are you working on?'}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                />
              </label>
            </div>
            <div className="field">
              <label>
                <span>{i18n?.t ? i18n.t('color') : 'Color'}</span>
                <input
                    id="favorite-color-edit"
                    type="color"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                />
              </label>
            </div>
          </div>
          <div className="row right">
            <button
              type="button"
              className="btn"
              onClick={cancelEditing}
            >
              {i18n?.t ? i18n.t('cancel') : 'Cancel'}
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={!canSaveEdit}
              onClick={handleEditSave}
              title={!canSaveEdit ? (i18n?.t ? i18n.t('invalidOrUnknownIssue') : 'Invalid or unknown issue') : undefined}
            >
              {i18n?.t ? i18n.t('save') : 'Save'}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteFavId}
        title={i18n?.t ? i18n.t('deleteFavoriteTitle') : 'Delete favorite?'}
        message={i18n?.t ? i18n.t('deleteFavoriteMessage') : 'Are you sure you want to delete this favorite? This action cannot be undone.'}
        confirmLabel={i18n?.t ? i18n.t('delete') : 'Delete'}
        cancelLabel={i18n?.t ? i18n.t('cancel') : 'Cancel'}
        onCancel={() => setDeleteFavId(null)}
        onConfirm={async () => {
          if (deleteFavId) {
            await removeFavorite(deleteFavId);
          }
          setDeleteFavId(null);
        }}
        confirmType="danger"
      />
    </div>
  );
};

export default FavoritesCard;
